import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { List } from 'react-window';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const resolveField = (field, item) => {
  if (!field || !item) return '';
  if (item[field] !== undefined) return item[field];
  const key = Object.keys(item).find(k => k.toLowerCase() === field.toLowerCase());
  return key ? item[key] : '';
};

/**
 * How many rows "load more as you scroll" should show so that `index` is included.
 * Rounds up to the next whole step so the list grows in the same increments the user sees.
 */
export const rowsToShow = (index, step) => Math.ceil((index + 1) / step) * step;

// Row renderer defined outside component for stable reference
const Row = ({ index, style, items, selectedItem, onSelectItem, primaryField, secondaryField, secondaryFormatter }) => {
  const item = items[index];
  const isSelected = selectedItem?.id === item.id;
  const primary = resolveField(primaryField, item) || '';
  let secondary = '';
  if (secondaryFormatter) {
    secondary = secondaryFormatter(item);
  } else if (secondaryField) {
    secondary = resolveField(secondaryField, item) || '';
  }
  return (
    <div style={{ ...style, overflow: 'hidden' }}>
      <button
        onClick={() => onSelectItem(item)}
        className={`stui-list-item ${isSelected ? 'selected' : ''}`}
      >
        <span className="stui-list-item-primary">{primary}</span>
        {secondary && <span className="stui-list-item-secondary">{secondary}</span>}
      </button>
    </div>
  );
};

/**
 * Virtualized list panel.
 *
 * Optional "load more as you scroll" (`loadStep`): the whole dataset is already in memory, but
 * handing react-window 26K rows makes the scrollbar a sliver where a nudge jumps thousands of
 * records. With a step, the list renders the first `loadStep` rows and adds the next step from
 * memory as the user nears the end, so the scrollbar shrinks gradually. `resetKey` changing (a new
 * search, filter or sort) starts over at one step; a selected record further down extends the list
 * far enough to include it. No step = every row, as before.
 */
const ListPanel = ({
  items,
  selectedItem,
  onSelectItem,
  primaryField,
  secondaryField,
  secondaryFormatter,
  collapsed,
  onToggleCollapse,
  scrollToSelected = false,
  loadStep = 0,
  resetKey,
  loadingMore = false,
  emptyText,
}) => {
  const listRef = useRef(null);
  const [shown, setShown] = useState(loadStep || Infinity);

  useEffect(() => {
    setShown(loadStep || Infinity);
  }, [resetKey, loadStep]);

  // A selected record past the rendered rows (deep link, a pick restored after a search is
  // cleared) pulls the list out far enough to include it.
  const selectedIndex = useMemo(
    () => (selectedItem ? items.findIndex(item => item.id === selectedItem.id) : -1),
    [items, selectedItem]
  );
  useEffect(() => {
    if (loadStep && selectedIndex >= shown) setShown(rowsToShow(selectedIndex, loadStep));
  }, [selectedIndex, shown, loadStep]);

  const visibleItems = useMemo(
    () => (shown >= items.length ? items : items.slice(0, shown)),
    [items, shown]
  );
  const hasMore = visibleItems.length < items.length;

  const handleRowsRendered = useCallback(({ stopIndex }) => {
    if (hasMore && stopIndex >= visibleItems.length - 10) {
      setShown(s => s + loadStep);
    }
  }, [hasMore, visibleItems.length, loadStep]);

  // Scroll to selected item on deep link navigation
  useEffect(() => {
    if (scrollToSelected && selectedIndex >= 0 && listRef.current) {
      setTimeout(() => {
        listRef.current?.scrollToRow({ index: selectedIndex, align: 'start' });
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToSelected, selectedItem?.id]);

  // Keyboard navigation — up/down arrows move between items, extending the list at its end
  const handleKeyDown = useCallback((e) => {
    if (!items || items.length === 0) return;
    const currentIndex = selectedIndex;
    let newIndex = currentIndex;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : currentIndex;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
    } else {
      return;
    }

    if (newIndex !== currentIndex && items[newIndex]) {
      onSelectItem(items[newIndex]);
      if (newIndex >= visibleItems.length) {
        // The extend effect grows the list on the next render; scroll once it has.
        setTimeout(() => listRef.current?.scrollToRow({ index: newIndex, align: 'auto' }), 0);
      } else {
        listRef.current?.scrollToRow({ index: newIndex, align: 'auto' });
      }
    }
  }, [items, selectedIndex, onSelectItem, visibleItems.length]);

  // Memoize rowProps to avoid unnecessary row re-renders
  const rowProps = useMemo(() => ({
    items: visibleItems,
    selectedItem,
    onSelectItem,
    primaryField,
    secondaryField,
    secondaryFormatter,
  }), [visibleItems, selectedItem, onSelectItem, primaryField, secondaryField, secondaryFormatter]);

  if (collapsed) {
    return (
      <div className="stui-list-panel stui-collapsed">
        <button
          onClick={onToggleCollapse}
          className="stui-list-panel-expand-btn"
          title="Expand list"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  }

  let status = '';
  if (loadingMore) status = 'Loading all records…';
  else if (hasMore) status = `Showing ${visibleItems.length.toLocaleString()} of ${items.length.toLocaleString()}`;

  return (
    <div
      className="stui-list-panel"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="stui-list-panel-header">
        {status && <span className="stui-text-sm-muted stui-ml-auto-mr">{status}</span>}
        <button
          onClick={onToggleCollapse}
          className="stui-list-panel-collapse-btn"
          title="Collapse list"
        >
          <ChevronLeft size={16} />
        </button>
      </div>
      <div className="stui-list-scroll">
        {items.length === 0 && emptyText ? (
          <div className="stui-list-empty">{emptyText}</div>
        ) : (
          <List
            listRef={listRef}
            rowComponent={Row}
            rowCount={visibleItems.length}
            rowHeight={44}
            rowProps={rowProps}
            onRowsRendered={loadStep ? handleRowsRendered : undefined}
          />
        )}
      </div>
    </div>
  );
};

export default ListPanel;
